<script setup>
import { ref, onMounted } from 'vue';
import { useScrollAnimations } from '@/composables/useScrollAnimations';
import LandingCtaButton from './LandingCtaButton.vue';
import LandingPageManagerDirector from '../../../assets/landingPageManagingDirector.png';
import LandingPageITManager from '../../../assets/LandingPageITManager.png';

const { animateInView, slideLeft, slideRight } = useScrollAnimations();
const sectionRef = ref(null);
const leftColRef = ref(null);
const rightColRef = ref(null);
const itLeftColRef = ref(null);
const itRightColRef = ref(null);

onMounted(() => {
    if (sectionRef.value) {
        animateInView(sectionRef.value, () => {
        });
    }

    if (leftColRef.value && rightColRef.value) {
        animateInView(leftColRef.value, () => {
            slideRight(leftColRef.value);
            slideLeft(rightColRef.value);
        });
    }

    if (itLeftColRef.value && itRightColRef.value) {
        animateInView(itLeftColRef.value, () => {
            slideRight(itLeftColRef.value);
            slideLeft(itRightColRef.value);
        });
    }
});
</script>

<template>
    <div class="py-24 relative overflow-hidden" ref="sectionRef">
        <div class="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">

            <!-- Header -->
            <div class="text-center mb-24 max-w-4xl mx-auto">
                <h2 class="text-3xl font-bold tracking-tight text-white sm:text-5xl mb-6"
                    v-html="$t('landingPage.v2.benefits.header.title')">
                </h2>
                <p class="text-lg text-gray-400 leading-relaxed max-w-2xl mx-auto">
                    {{ $t('landingPage.v2.benefits.header.subtitle') }}
                </p>
            </div>

            <!-- Managing Directors Section -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-32">
                <!-- Image Side (Left) -->
                <div ref="leftColRef" class="order-2 lg:order-1 relative opacity-0">
                    <div class="relative w-full aspect-square max-w-xl mx-auto">
                        <div class="absolute inset-0 bg-blue-600/20 rounded-full blur-3xl -z-10"></div>
                        <div class="w-full h-full flex items-center justify-center ">
                            <img :src="LandingPageManagerDirector" alt="Managing Director">
                        </div>
                    </div>
                </div>

                <!-- Content Side (Right)-->
                <div ref="rightColRef" class="order-1 lg:order-2 opacity-0">
                    <h3 class="text-2xl font-bold text-white mb-8">{{ $t('landingPage.v2.benefits.directors.title') }}
                    </h3>
                    <ul class="space-y-8">
                        <li class="flex gap-4">
                            <!-- Icon -->
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                                stroke="#2CA58D" class="w-6 h-6 flex-shrink-0 mt-1">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m3.75 9v6m3-3H9m1.5-12H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                            </svg>
                            <div>
                                <h4 class="text-lg font-semibold text-white mb-2">{{
                                    $t('landingPage.v2.benefits.directors.items.liability.title') }}</h4>
                                <p class="text-gray-400">{{ $t('landingPage.v2.benefits.directors.items.liability.desc')
                                    }}</p>
                            </div>
                        </li>
                        <li class="flex gap-4">
                            <!-- Icon -->
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                                stroke="#2CA58D" class="w-6 h-6 flex-shrink-0 mt-1">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
                            </svg>
                            <div>
                                <h4 class="text-lg font-semibold text-white mb-2">{{
                                    $t('landingPage.v2.benefits.directors.items.compliance.title') }}</h4>
                                <p class="text-gray-400">{{
                                    $t('landingPage.v2.benefits.directors.items.compliance.desc') }}</p>
                            </div>
                        </li>
                        <li class="flex gap-4">
                            <!-- Icon -->
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                                stroke="#2CA58D" class="w-6 h-6 flex-shrink-0 mt-1">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                                <h4 class="text-lg font-semibold text-white mb-2">{{
                                    $t('landingPage.v2.benefits.directors.items.cost.title') }}</h4>
                                <p class="text-gray-400">{{ $t('landingPage.v2.benefits.directors.items.cost.desc') }}
                                </p>
                            </div>
                        </li>
                        <li class="flex gap-4">
                            <!-- Icon -->
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                                stroke="#2CA58D" class="w-6 h-6 flex-shrink-0 mt-1">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                            </svg>
                            <div>
                                <h4 class="text-lg font-semibold text-white mb-2">{{
                                    $t('landingPage.v2.benefits.directors.items.dashboard.title') }}</h4>
                                <p class="text-gray-400">{{
                                    $t('landingPage.v2.benefits.directors.items.dashboard.desc') }}</p>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>

            <!-- IT Managers Section -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <!-- Content Side (Left) -->
                <div ref="itLeftColRef" class="opacity-0">
                    <h3 class="text-2xl font-bold text-white mb-8">{{ $t('landingPage.v2.benefits.itManagers.title') }}
                    </h3>
                    <ul class="space-y-8">
                        <li class="flex gap-4">
                            <!-- Icon -->
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                                stroke="#2CA58D" class="w-6 h-6 flex-shrink-0 mt-1">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M5.25 14.25h13.5m-13.5 0a3 3 0 01-3-3m3 3a3 3 0 003 3h13.5a3 3 0 003-3m-16.5-6.75h16.5a3 3 0 013 3v2.25a3 3 0 01-3 3h-16.5a3 3 0 01-3-3V9.75a3 3 0 013-3zM9 11.25v1.5M12 9v3.75m3-6v6" />
                            </svg>
                            <div>
                                <h4 class="text-lg font-semibold text-white mb-2">{{
                                    $t('landingPage.v2.benefits.itManagers.items.integration.title') }}</h4>
                                <p class="text-gray-400">{{
                                    $t('landingPage.v2.benefits.itManagers.items.integration.desc') }}</p>
                            </div>
                        </li>
                        <li class="flex gap-4">
                            <!-- Icon -->
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                                stroke="#2CA58D" class="w-6 h-6 flex-shrink-0 mt-1">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.855.142 1.205-.108l.737-.527a1.125 1.125 0 011.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.25.35-.272.806-.107 1.204.165.397.505.71.93.78l.893.15c.543.09.94.56.94 1.109v1.094c0 .55-.397 1.02-.94 1.11l-.893.149c-.425.07-.765.383-.93.78-.165.398-.143.854.107 1.204l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 01-1.449.12l-.738-.527c-.35-.25-.806-.272-1.203-.107-.397.165-.71.505-.781.929l-.149.894c-.09.542-.56.94-1.11.94h-1.094c-.55 0-1.02-.398-1.11-.94l-.149-.894c-.07-.424-.384-.764-.78-.93-.398-.164-.855-.142-1.204.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 01-.12-1.45l.527-.737c.25-.35.273-.806.108-1.204-.165-.397-.505-.71-.929-.78l-.894-.15c-.542-.09-.94-.56-.94-1.109v-1.094c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.765-.383.93-.78.165-.398.143-.854-.107-1.204l-.527-.738a1.125 1.125 0 01.12-1.45l.773-.773a1.125 1.125 0 011.45-.12l.737.527c.35.25.807.272 1.204.107.397-.165.71-.505.78-.929l.15-.894z" />
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <div>
                                <h4 class="text-lg font-semibold text-white mb-2">{{
                                    $t('landingPage.v2.benefits.itManagers.items.automation.title') }}</h4>
                                <p class="text-gray-400">{{
                                    $t('landingPage.v2.benefits.itManagers.items.automation.desc') }}</p>
                            </div>
                        </li>
                        <li class="flex gap-4">
                            <!-- Icon -->
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                                stroke="#2CA58D" class="w-6 h-6 flex-shrink-0 mt-1">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                                <h4 class="text-lg font-semibold text-white mb-2">{{
                                    $t('landingPage.v2.benefits.itManagers.items.relief.title') }}</h4>
                                <p class="text-gray-400">{{ $t('landingPage.v2.benefits.itManagers.items.relief.desc')
                                    }}</p>
                            </div>
                        </li>
                        <li class="flex gap-4">
                            <!-- Icon -->
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                                stroke="#2CA58D" class="w-6 h-6 flex-shrink-0 mt-1">
                                <path stroke-linecap="round" stroke-linejoin="round"
                                    d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                                <h4 class="text-lg font-semibold text-white mb-2">{{
                                    $t('landingPage.v2.benefits.itManagers.items.incidents.title') }}</h4>
                                <p class="text-gray-400">{{
                                    $t('landingPage.v2.benefits.itManagers.items.incidents.desc')
                                    }}</p>
                            </div>
                        </li>
                    </ul>
                </div>

                <!-- Image Side (Right) -->
                <div ref="itRightColRef" class="opacity-0">
                    <div class="relative w-full aspect-square max-w-xl mx-auto">
                        <div class="absolute inset-x-0 bottom-0 top-1/2 bg-blue-600/10 rounded-full blur-3xl -z-10">
                        </div>
                        <div class="w-full h-full flex items-center justify-center">
                            <img :src="LandingPageITManager" alt="IT Manager">
                        </div>
                    </div>
                </div>
            </div>
            <div class="mt-16 flex items-center justify-center">
                <LandingCtaButton sectionName="Benefits" :text="$t('landingPage.v2.cta.benefits')" />
            </div>
        </div>
    </div>
</template>

<style scoped></style>
